import { makeAutoObservable, runInAction } from 'mobx'

import { serverMessage } from '@/Ship/Helpers/serverError'

import { LoadDiskPolicyTask } from '../Tasks/LoadDiskPolicyTask'
import { UpdateDiskPolicyTask } from '../Tasks/UpdateDiskPolicyTask'
import { policyChanges } from '../Helpers/diskAdminState'

import type { DiskPolicy, DiskPolicyValues } from '../Contracts/DiskAdmin'

/** Налаштування диска тенанта (`DISK-006` §3.10): форма над `GET/PATCH policy`. */
export class DiskPolicyStore {
    policy: DiskPolicy | null = null
    draft: DiskPolicyValues | null = null
    isLoading = false
    isSaving = false
    error: string | null = null
    saveError: string | null = null

    constructor() {
        makeAutoObservable(this)
    }

    get changes(): Partial<DiskPolicyValues> {
        return this.policy && this.draft ? policyChanges(this.policy.policy, this.draft) : {}
    }

    get isDirty(): boolean {
        return Object.keys(this.changes).length > 0
    }

    async load(): Promise<void> {
        this.isLoading = true
        this.error = null

        try {
            const policy = await LoadDiskPolicyTask()

            runInAction(() => {
                this.policy = policy
                this.draft = { ...policy.policy, reminderDays: [...policy.policy.reminderDays] }
            })
        } catch (error) {
            runInAction(() => {
                this.error = serverMessage(error, 'Не вдалося завантажити налаштування диска')
            })
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }

    set<K extends keyof DiskPolicyValues>(key: K, value: DiskPolicyValues[K]): void {
        if (this.draft) this.draft = { ...this.draft, [key]: value }
        this.saveError = null
    }

    reset(): void {
        if (this.policy) this.draft = { ...this.policy.policy, reminderDays: [...this.policy.policy.reminderDays] }
        this.saveError = null
    }

    /** Зберегти змінене; `true` — збережено. */
    async save(): Promise<boolean> {
        const changes = this.changes

        if (Object.keys(changes).length === 0) return true

        this.isSaving = true
        this.saveError = null

        try {
            const policy = await UpdateDiskPolicyTask(changes)

            runInAction(() => {
                this.policy = policy
                this.draft = { ...policy.policy, reminderDays: [...policy.policy.reminderDays] }
            })

            return true
        } catch (error) {
            runInAction(() => {
                this.saveError = serverMessage(error, 'Не вдалося зберегти налаштування')
            })

            return false
        } finally {
            runInAction(() => {
                this.isSaving = false
            })
        }
    }
}

export const diskPolicyStore = new DiskPolicyStore()
